import { useState } from "react";
import { api } from "../../api/apiClient";
import type { InvoicePaymentsView, SupplierPaymentMethod } from "../../api/types";
import { paymentMethodLabel, SUPPLIER_PAYMENT_METHODS } from "../../utils/invoicePaymentText";
import { ReasonModal } from "../ui/ReasonModal";

function localIsoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * G-121 S4 (OD-146): record one payment against a factura — the day the money
 * left (today by default, never later), the amount (what is still owed by
 * default), how it was paid (no default), and an optional note. Shared by the
 * factura's page and the Finances card. The house `ReasonModal`, with its
 * optional reason as the note.
 */
export function RecordInvoicePaymentModal({
  invoiceId,
  suggestedAmountEur,
  onSaved,
  onClose,
}: {
  invoiceId: string;
  suggestedAmountEur: string | null;
  onSaved: (view: InvoicePaymentsView) => void;
  onClose: () => void;
}) {
  const today = localIsoDay(new Date());
  const [paidOn, setPaidOn] = useState(today);
  const [amount, setAmount] = useState(suggestedAmountEur ?? "");
  const [method, setMethod] = useState<SupplierPaymentMethod | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (note: string | undefined) => {
    const amountEur = Number(amount);
    if (!Number.isFinite(amountEur) || amountEur <= 0) {
      setError("Enter the amount paid.");
      return;
    }
    if (!method) {
      setError("Say how it was paid.");
      return;
    }
    if (!paidOn || paidOn > today) {
      setError("A payment cannot be dated after today.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const view = await api.recordInvoicePayment(invoiceId, {
        paidOn,
        amountEur: Math.round(amountEur * 100) / 100,
        method,
        ...(note ? { note } : {}),
      });
      onSaved(view);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not record the payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ReasonModal
      title="Record a payment"
      reasonMode="optional"
      reasonLabel="Note"
      confirmLabel="Record payment"
      busy={busy}
      error={error}
      onConfirm={save}
      onClose={onClose}
    >
      <div className="inv-payment-form">
        <div className="field">
          <label htmlFor="inv-pay-date">Paid on</label>
          <input id="inv-pay-date" type="date" max={today} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="inv-pay-amount">Amount (€)</label>
          <input
            id="inv-pay-amount"
            type="number"
            inputMode="decimal"
            min={0.01}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="inv-pay-method">How</label>
          <select
            id="inv-pay-method"
            value={method}
            onChange={(e) => setMethod(e.target.value as SupplierPaymentMethod | "")}
          >
            <option value="">Choose…</option>
            {SUPPLIER_PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {paymentMethodLabel(m)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </ReasonModal>
  );
}
